import { useState } from 'react'
import { Bug } from 'lucide-react'
import * as Sentry from '@sentry/react'
import { api } from '@/shared/lib/api'

type TestResult = {
  target: 'frontend' | 'backend'
  status: 'success' | 'error' | 'disabled'
  message: string
}

export function SentryTestButton() {
  const [results, setResults] = useState<TestResult[]>([])
  const [isTesting, setIsTesting] = useState(false)

  const testFrontend = (): TestResult => {
    const dsn = import.meta.env.VITE_SENTRY_DSN
    if (!dsn) {
      return { target: 'frontend', status: 'disabled', message: 'VITE_SENTRY_DSN no configurado' }
    }
    try {
      Sentry.captureException(new Error('Sentry test error from Club frontend'))
      return { target: 'frontend', status: 'success', message: 'Error enviado a Sentry' }
    } catch {
      return { target: 'frontend', status: 'error', message: 'Error al enviar a Sentry' }
    }
  }

  const testBackend = async (): Promise<TestResult> => {
    try {
      const { data } = await api.get('/sentry-test/')
      if (data.status === 'disabled') {
        return { target: 'backend', status: 'disabled', message: data.message }
      }
      return { target: 'backend', status: 'success', message: 'Error enviado a Sentry' }
    } catch (err) {
      const status = (err as { response?: { status?: number } }).response?.status
      if (status === 500) {
        return { target: 'backend', status: 'success', message: 'Error enviado a Sentry (500 esperado)' }
      }
      return { target: 'backend', status: 'error', message: `Error inesperado: ${status}` }
    }
  }

  const handleTest = async () => {
    setIsTesting(true)
    setResults([])
    const frontendResult = testFrontend()
    const backendResult = await testBackend()
    setResults([frontendResult, backendResult])
    setIsTesting(false)
  }

  const statusColor = (s: TestResult['status']) => {
    if (s === 'success') return 'text-green-700 bg-green-50'
    if (s === 'disabled') return 'text-yellow-700 bg-yellow-50'
    return 'text-red-700 bg-red-50'
  }

  const statusLabel = (s: TestResult['status']) => {
    if (s === 'success') return 'OK'
    if (s === 'disabled') return 'Desactivado'
    return 'Error'
  }

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-600">
            Envia un error de prueba a Sentry desde el frontend y el backend
            para verificar que la integracion funciona correctamente.
          </p>
        </div>
        <button
          onClick={handleTest}
          disabled={isTesting}
          className="btn btn-secondary flex items-center gap-2 ml-4 shrink-0"
        >
          {isTesting ? (
            <>
              <div className="w-4 h-4 border-2 border-gray-400/30 border-t-gray-400 rounded-full animate-spin" />
              Probando...
            </>
          ) : (
            <>
              <Bug size={16} />
              Probar Sentry
            </>
          )}
        </button>
      </div>

      {results.length > 0 && (
        <div className="mt-3 space-y-2">
          {results.map((r) => (
            <div
              key={r.target}
              className={`flex items-center justify-between p-3 rounded-lg text-sm ${statusColor(r.status)}`}
            >
              <span className="font-medium capitalize">{r.target}</span>
              <span>
                {statusLabel(r.status)} — {r.message}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
